import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Navigation, ShieldCheck, Heart, Facebook, Store, BadgePercent, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FB_MEMBERS_DISPLAY } from "@/lib/brand";

// Pastilles thématiques rapides du hero (liens vers les univers correspondants).
const QUICK_PILLS = [
  { label: "Restaurants", to: "/categories/restauration" },
  { label: "Artisans", to: "/categories/artisans-services" },
  { label: "Beauté", to: "/categories/beaute-bien-etre" },
  { label: "Maison", to: "/categories/maison-jardin" },
  { label: "Loisirs", to: "/categories/loisirs-culture" },
  { label: "Commerces", to: "/partenaires" },
];

// Carte « Bon plan vedette » : TOUJOURS une offre RÉELLE publiée (jamais mockée).
// Priorise une offre proche du visiteur SI la géoloc est déjà autorisée (sans popup intrusive).
function HeroFeaturedDeal() {
  const [offer, setOffer] = useState(null);
  const [geo, setGeo] = useState(false);     // vrai si l'offre affichée est géolocalisée
  const [ready, setReady] = useState(false);

  // Repli : tirage aléatoire parmi les offres les plus populaires.
  const loadPopular = useCallback(() =>
    api.get("/offers?sort=popular&limit=12")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data.filter((o) => o && o.id) : [];
        setOffer(list.length ? list[Math.floor(Math.random() * list.length)] : null);
        setGeo(false);
      })
      .catch(() => setOffer(null))
      .finally(() => setReady(true)), []);

  // Offres proches du visiteur (triées par distance côté backend), tirage parmi les plus proches.
  const loadNearby = useCallback((lat, lng) =>
    api.get(`/offers?near_lat=${lat}&near_lng=${lng}&limit=12`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data.filter((o) => o && o.id) : [];
        const near = list.filter((o) => o.distance_km != null);
        const pool = (near.length ? near : list).slice(0, 6);
        if (pool.length) {
          setOffer(pool[Math.floor(Math.random() * pool.length)]);
          setGeo(near.length > 0);
          setReady(true);
        } else {
          loadPopular();
        }
      })
      .catch(() => loadPopular()), [loadPopular]);

  useEffect(() => {
    // La carte vedette se personnalise automatiquement si la géoloc est DÉJÀ autorisée
    // (l'opt-in se fait via le bloc « Autour de moi », pas ici — aucune popup forcée).
    if (navigator.geolocation && navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" })
        .then((res) => {
          if (res.state === "granted") {
            navigator.geolocation.getCurrentPosition(
              ({ coords }) => loadNearby(coords.latitude, coords.longitude),
              () => loadPopular(),
              { timeout: 6000, maximumAge: 300000 }
            );
          } else {
            loadPopular();
          }
        })
        .catch(() => loadPopular());
    } else {
      loadPopular();
    }
  }, [loadNearby, loadPopular]);

  if (!ready) return null;

  // Repli SANS fausse offre : simple carte d'accès aux bons plans réels.
  if (!offer) {
    return (
      <Link to="/bons-plans" data-testid="hero-featured-fallback" className="block rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50 p-6 text-center hover:border-[#E05638]/40 transition-colors">
        <span className="w-12 h-12 mx-auto rounded-2xl bg-[#FFF4EC] text-[#E05638] grid place-items-center"><BadgePercent size={24} /></span>
        <div className="mt-3 font-heading text-lg font-extrabold text-[#172A3A]">Découvrez les bons plans locaux</div>
        <p className="mt-1 text-sm text-slate-500">Les offres de vos commerçants d'Eure-et-Loir, au même endroit.</p>
        <span className="mt-4 inline-flex items-center gap-1.5 font-bold text-[#E05638] text-sm">Voir les bons plans <ArrowRight size={15} /></span>
      </Link>
    );
  }

  const img = offer.image || (offer.images && offer.images[0]) || null;
  const merchant = offer.business?.name;
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50 overflow-hidden animate-in fade-in slide-in-from-bottom-3 duration-700" data-testid="hero-featured-deal">
      <div className="relative aspect-[16/10] bg-slate-100">
        {img && <img src={img} alt={offer.title} className="w-full h-full object-cover" />}
        {offer.discount && (
          <span className="absolute top-3 left-3 rounded-full bg-[#E05638] text-white text-sm font-extrabold px-3 py-1 shadow-md" data-testid="hero-featured-discount">{offer.discount}</span>
        )}
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur text-[#172A3A] text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 shadow-sm" data-testid="hero-featured-proximity">
          <Navigation size={11} className="text-[#E05638]" /> {geo && offer.distance_km != null ? `À ${offer.distance_km} km` : "Près de chez vous"}
        </span>
      </div>
      <div className="p-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-[#E05638]">Bon plan vedette</div>
        <h3 className="mt-1 font-heading text-lg font-extrabold text-[#172A3A] leading-tight line-clamp-2">{offer.title}</h3>
        {(merchant || offer.city) && (
          <div className="mt-1.5 text-sm text-slate-500 inline-flex items-center gap-1.5">
            <Store size={13} className="text-[#E05638]" /> {[merchant, offer.city].filter(Boolean).join(" · ")}
          </div>
        )}
        <Link to={`/offre/${offer.id}`} data-testid="hero-featured-deal-cta">
          <Button className="mt-4 w-full h-11 btn-shine text-white font-bold rounded-xl">En profiter</Button>
        </Link>
      </div>
    </div>
  );
}

export default function HeroSearch({ content = {}, stats = {} }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (city) params.set("city", city);
    nav(`/bons-plans?${params.toString()}`);
  };

  return (
    <section
      className="relative overflow-hidden bg-[#FAF8F5]"
      data-testid="hero-section"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10 md:pt-12 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* Colonne gauche : accroche + recherche (compactée, alignée à gauche) */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E4E4E7] text-[#0F0F11] text-xs font-bold uppercase tracking-widest shadow-sm animate-in fade-in slide-in-from-top-2 duration-500">
              <ShieldCheck size={14} className="text-[#E05638]" /> {content["home.hero.eyebrow"] || "100 % Local · Eure-et-Loir"}
            </span>

            <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0F0F11] leading-[1.08] tracking-tight animate-in fade-in slide-in-from-bottom-3 duration-700" data-testid="hero-title">
              La vie locale,<br className="sm:hidden" /> <span className="text-[#E05638]">à portée de clic.</span>
            </h1>

            <p className="mt-3 text-base md:text-lg text-slate-600 max-w-xl mx-auto lg:mx-0">
              {content["home.hero.subtitle"] || "Commerces, artisans, bons plans, événements et services près de chez vous, partout en Eure-et-Loir."}
            </p>

            {/* Barre de recherche multi-critères */}
            <form onSubmit={submit} data-testid="hero-search-form" className="mt-5 max-w-xl mx-auto lg:mx-0 bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-200/50 p-2.5 flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2 flex-1 px-2">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <Input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Que cherchez-vous ? (restaurant, coiffeur…)" className="border-0 shadow-none focus-visible:ring-0 h-11 p-0" />
              </div>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger data-testid="hero-commune-select" className="sm:w-44 h-11 border-slate-200 rounded-xl">
                  <span className="inline-flex items-center gap-1.5 text-slate-600"><MapPin size={14} className="text-[#E05638]" /><SelectValue placeholder="Commune" /></span>
                </SelectTrigger>
                <SelectContent>{cities.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
              <Button type="submit" data-testid="hero-submit-btn" className="h-11 px-6 btn-shine text-white font-bold rounded-xl">Rechercher</Button>
            </form>

            {/* Autour de moi + pastilles rapides */}
            <div className="mt-4 flex flex-wrap items-center justify-center lg:justify-start gap-2">
              <Link to="/autour-de-moi" data-testid="hero-geoloc-btn" className="group inline-flex items-center gap-1.5 rounded-full bg-[#0F0F11] text-white px-4 py-2 text-sm font-semibold hover:bg-black transition-colors active:scale-[0.98]">
                <Navigation size={15} className="text-[#E05638]" /> Autour de moi
              </Link>
              {QUICK_PILLS.map((p) => (
                <Link key={p.label} to={p.to} data-testid="hero-suggestion-pill" className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-[#27272A] hover:border-[#E05638]/50 hover:text-[#E05638] transition-colors active:scale-[0.98]">
                  {p.label}
                </Link>
              ))}
            </div>

            {/* Inscription mise en avant + preuve sociale Facebook */}
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-center lg:justify-start">
              <Link to="/inscription" data-testid="hero-register-link">
                <Button className="h-12 px-7 btn-shine text-white font-extrabold rounded-xl text-base w-full sm:w-auto">
                  <Heart size={18} className="mr-2" /> Créez votre compte gratuit
                </Button>
              </Link>
              <span className="text-sm text-slate-700">Déjà membre ? <Link to="/connexion" className="font-bold text-[#0F0F11] hover:underline" data-testid="hero-login-link">Se connecter</Link></span>
            </div>

            <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate-600 bg-white/80 backdrop-blur-sm px-3.5 py-1.5 rounded-full ring-1 ring-black/5" data-testid="hero-social-proof">
              <Facebook size={16} className="text-[#1877F2]" />
              <span><strong className="text-[#0F0F11]">{stats.fb_members_display || FB_MEMBERS_DISPLAY}</strong> membres · <strong className="text-[#0F0F11]">{stats.fb_groups_display || stats.fb_groups || "25"}</strong> groupes Facebook locaux</span>
            </div>
          </div>

          {/* Colonne droite : bon plan vedette RÉEL (dynamique) */}
          <div className="lg:col-span-5">
            <HeroFeaturedDeal />
          </div>
        </div>
      </div>
    </section>
  );
}
